import * as React from 'react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import MyButton from './buttons'
import MySelect from './MySelect'
import MyInput from './MyInput'
import { IoMdHelp } from 'react-icons/io'
import { FaUser, FaEnvelope, FaInfoCircle } from 'react-icons/fa'
import MyTextArea from './MyTextArea'
import Modal from './MyModal'
import CompanyInfoModalContent from './CompanyInfoModal'
import { contactService } from '../../services/contactService'
import { useLocation } from 'react-router-dom'

type SubmissionStatus = 'idle' | 'success' | 'error'

interface ContactFormProps {
  variant?: 'redpink' | 'white'
  className?: string
  showInfoButton?: boolean
  showTitle?: boolean
}

const ContactForm: React.FC<ContactFormProps> = ({
  variant = 'redpink',
  className = '',
  showInfoButton = true,
  showTitle = true,
}) => {
  const { t } = useTranslation()
  const [formErrors] = useState<{ [key: string]: string }>({})
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false)
  const [submissionStatus, setSubmissionStatus] =
    useState<SubmissionStatus>('idle')
  const [lastSubmitTime, setLastSubmitTime] = useState<number>(() => {
    const saved = localStorage.getItem('lastContactSubmitTime')
    return saved ? parseInt(saved) : 0
  })
  const [timeRemaining, setTimeRemaining] = useState<number>(0)
  const [canSubmit, setCanSubmit] = useState<boolean>(true)
  const [isFormValid, setIsFormValid] = useState(false)
  const location = useLocation()
  const [formData, setFormData] = useState({
    firstname: '',
    title: '',
    description: '',
    email: '',
  })
  const textColor =
    location.pathname === '/contact' ? 'text-anthracite' : 'text-white'
  const styles = {
    container: {
      redpink: 'bg-redpink text-white',
      white: 'bg-white text-anthracite',
    },
    input: {
      redpink: 'text-anthracite !bg-grisclair hover:!bg-white',
      white: 'text-anthracite !bg-grisclair hover:!bg-gray-100',
    },
    select: {
      redpink: 'text-anthracite !bg-white hover:!bg-white',
      white: 'text-anthracite !bg-grisclair hover:!bg-gray-100',
    },
    button: {
      redpink: 'bg-anthracite hover:bg-anthraciteLight text-white',
      white: 'bg-redpink hover:bg-redpinkDark text-white',
    },
    icon: {
      redpink: 'text-white hover:text-anthracite',
      white: 'text-redpink hover:text-anthraciteDark',
    },
    textarea: {
      redpink: 'text-anthracite !bg-white hover:!bg-white',
      white: 'text-anthracite !bg-grisclair hover:!bg-gray-100',
    },
  }

  const containerStyle = styles.container[variant]
  const inputStyle = styles.input[variant]
  const selectStyle = styles.select[variant]
  const buttonStyle = styles.button[variant]
  const iconStyle = styles.icon[variant]
  const textareaStyle = styles.textarea[variant]

  const subjectOptions = [
    { value: 'question', label: t('subject-question') },
    { value: 'suggestion', label: t('subject-suggestion') },
    { value: 'problem', label: t('subject-problem') },
    { value: 'other', label: t('subject-other') },
  ]

  useEffect(() => {
    const checkCooldown = () => {
      const now = Date.now()
      const timePassed = now - lastSubmitTime
      const cooldownPeriod = 5 * 60 * 1000 // 5 minutes

      if (timePassed < cooldownPeriod) {
        setCanSubmit(false)
        setTimeRemaining(Math.ceil((cooldownPeriod - timePassed) / 1000))
      } else {
        setCanSubmit(true)
        setTimeRemaining(0)
      }
    }

    const timer = setInterval(checkCooldown, 1000)
    checkCooldown()

    return () => clearInterval(timer)
  }, [lastSubmitTime])

  const handleChange = (field: string) => (value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleSubjectChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      title: value,
    }))
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!canSubmit) {
      setSubmissionStatus('error')
      setIsStatusModalOpen(true)
      return
    }

    if (!isFormValid) {
      setSubmissionStatus('error')
      setIsStatusModalOpen(true)
      return
    }

    try {
      const updatedFormData = {
        ...formData,
        title: `(anonymous-user) ${formData.title}`,
      }
      await contactService.sendContactForm(updatedFormData)

      const now = Date.now()
      setLastSubmitTime(now)
      localStorage.setItem('lastContactSubmitTime', now.toString())

      setSubmissionStatus('success')
      setIsStatusModalOpen(true)
      setFormData({
        firstname: '',
        title: '',
        description: '',
        email: '',
      })
    } catch (error) {
      console.error("Erreur lors de l'envoi du formulaire:", error)
      setSubmissionStatus('error')
      setIsStatusModalOpen(true)
    }
  }

  const handleCloseStatusModal = () => {
    setIsStatusModalOpen(false)
    if (submissionStatus === 'success') {
      setFormData({
        firstname: '',
        title: '',
        description: '',
        email: '',
      })
    }
  }

  useEffect(() => {
    const isValid =
      formData.firstname.trim() !== '' &&
      formData.email.trim() !== '' &&
      formData.description.trim() !== '' &&
      formData.title.trim() !== ''
    setIsFormValid(isValid)
  }, [formData])

  return (
    <div
      className={`w-full mx-auto justify-center pb-5 sm:pb-10 mt-5 ${containerStyle} ${className}`}
    >
      {showTitle && (
        <div className='relative max-w-7xl mx-auto pb-5 pt-3'>
          {showInfoButton && (
            <button
              onClick={() => setIsModalOpen(true)}
              className={`absolute right-2 xl:right-0 top-2 xl:top-10 transition-colors ${iconStyle}`}
              aria-label="Informations sur l'entreprise"
            >
              <FaInfoCircle size={24} />
            </button>
          )}

          <>
            <h1 className='text-center text-2xl xl:text-4xl font-medium pt-5 block w-full'>
              {t('contact-us')}
            </h1>
            <h3 className='text-center text-white text-sm xl:text-base pb-3 font-normal block w-full'>
              {t('contact-description')}
            </h3>
          </>
        </div>
      )}

      <form onSubmit={handleSubmit} className='max-w-7xl px-2 xl:px-0 mx-auto'>
        <div className='flex flex-col gap-5'>
          <div className='flex flex-col md:flex-row gap-5'>
            <div className='w-full md:w-1/2'>
              <MyInput
                label={t('name-firstname-nickname')}
                value={formData.firstname}
                onChange={handleChange('firstname')}
                className={inputStyle}
                icon={<FaUser />}
                required
                error={formErrors['name']}
                autoComplete='name'
              />
            </div>

            <div className='w-full md:w-1/2'>
              <MyInput
                label={t('email')}
                value={formData.email}
                onChange={handleChange('email')}
                type='email'
                className={inputStyle}
                icon={<FaEnvelope />}
                required
                autoComplete='email'
              />
            </div>
          </div>

          <div className='w-full'>
            <MySelect
              label={t('subject')}
              options={subjectOptions}
              value={formData.title}
              onChange={handleSubjectChange}
              required
              icon={<IoMdHelp />}
              className={`h-10 ${selectStyle}`}
            />
          </div>

          <div className='w-full [&>div]:mb-0'>
            <MyTextArea
              className={`rounded w-full ${textareaStyle}`}
              label={t('message')}
              multiline
              placeholder={t('message')}
              name='message'
              value={formData.description}
              onChange={(value: string) => handleChange('description')(value)}
              required
              variant='grisclair'
              InputProps={{
                style: {
                  height: 'calc(3 * 40px + 2 * 16px)',
                  alignItems: 'flex-start',
                },
              }}
            />
            <span
              className={`w-full block text-center text-xs ${textColor} mt-1`}
            >
              {t('message-sms')}
            </span>
          </div>

          <div className='flex flex-col md:flex-row justify-center'>
            <div className='w-full md:w-1/3 px-2 text-center'>
              <MyButton
                variant={variant === 'redpink' ? 'anthracite' : 'redpink'}
                className={`w-full transition-all p-2 ${buttonStyle}`}
                type='submit'
                disabled={!isFormValid || !canSubmit}
              >
                {!canSubmit
                  ? `${t('send')} (${Math.floor(timeRemaining / 60)}:${(
                      timeRemaining % 60
                    )
                      .toString()
                      .padStart(2, '0')})`
                  : t('send')}
              </MyButton>
            </div>
          </div>
        </div>
      </form>
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <CompanyInfoModalContent onClose={() => setIsModalOpen(false)} />
      </Modal>
      <Modal isOpen={isStatusModalOpen} onClose={handleCloseStatusModal}>
        <div className='bg-white rounded-lg'>
          <h2 className='bg-anthracite text-white p-2 font-semibold text-base lg:text-xl text-center'>
            {t('submission-status')}
          </h2>
          <div className='space-y-4 p-5'>
            <p
              className={`text-center rounded-lg p-2 mb-4 ${
                submissionStatus === 'success'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-redlight text-red-800'
              }`}
            >
              {submissionStatus === 'success'
                ? t('contact-form-success')
                : !canSubmit
                ? t('wait-before-next-message', {
                    minutes: Math.floor(timeRemaining / 60),
                    seconds: (timeRemaining % 60).toString().padStart(2, '0'),
                  })
                : !isFormValid
                ? t('all-fields-required')
                : t('contact-form-error')}
            </p>

            <div className='flex justify-end'>
              <button
                onClick={handleCloseStatusModal}
                className='bg-anthracite text-white px-4 py-2 rounded hover:bg-opacity-90'
              >
                {t('close')}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default ContactForm
